import React, { useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { BoatMeasurementGuide } from '../components/blc/BoatMeasurementGuide';
import { BLCMasterData } from '../components/blc/BLCMasterData';
import { BLCMasterData as BLCMasterDataType, BOAT_TYPES, BoatHullType, createDefaultMasterData } from '../data/blcTables';
import { BLCMeasurement, calculateBLC } from '../utils/blcCalculation';
import { AlertTriangleIcon, CalculatorIcon, CheckCircleIcon, ChevronDownIcon, LockIcon, ShieldAlertIcon, ShipIcon } from 'lucide-react';

// Mock vessel data for lookup
const vesselData: Record<string, {
  name: string;
  type: string;
  regNo: string;
  owner: string;
  hullType: BoatHullType;
  engineType: string;
  enginePower: string;
  status: string;
}> = {
  'VSL-001': {
    name: 'MV Sundarban-1',
    type: 'Boat',
    regNo: 'REG-2024-001',
    owner: 'Rahim Uddin',
    hullType: 'Type C',
    engineType: 'Diesel',
    enginePower: '120 HP',
    status: 'Active'
  },
  'VSL-002': {
    name: 'FV Padma-3',
    type: 'Trawler',
    regNo: 'REG-2023-089',
    owner: 'Karim Sheikh',
    hullType: 'Type A',
    engineType: 'Diesel',
    enginePower: '250 HP',
    status: 'Active'
  },
  'VSL-003': {
    name: 'BT Meghna-7',
    type: 'Launch',
    regNo: 'REG-2024-015',
    owner: 'Abdul Hakim',
    hullType: 'Type B',
    engineType: 'Diesel',
    enginePower: '350 HP',
    status: 'Active'
  }
};
const VESSEL_TYPES = ['Boat', 'Launch', 'Ship', 'Trawler'] as const;
type Tab = 'list' | 'issue' | 'renew' | 'master';
export function BLCManagement() {
  const [activeTab, setActiveTab] = useState<Tab>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [master, setMaster] = useState<BLCMasterDataType>(() => createDefaultMasterData());
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [porCheckStatus, setPorCheckStatus] = useState<'pending' | 'clear' | 'flagged'>('pending');
  const [formData, setFormData] = useState({
    vesselNo: '',
    vesselType: '',
    ownerNid: '',
    ownerName: '',
    blcNumber: ''
  });
  const [measurement, setMeasurement] = useState<BLCMeasurement>({
    hullType: '',
    le: '',
    bf: '',
    bm: '',
    bb: '',
    de: '',
    dl: '',
    purpose: 'actual'
  });
  const matchedVessel = vesselData[formData.vesselNo] || null;
  const result = useMemo(() => calculateBLC(measurement, master), [measurement, master]);
  const fieldsLocked = measurement.hullType === '';
  const de = parseFloat(measurement.de);
  const dl = parseFloat(measurement.dl);
  const depthError = Number.isFinite(de) && Number.isFinite(dl) && dl >= de ? 'Dl must be less than De — the loaded waterline distance is always smaller than the empty one.' : '';
  const dlLabel = measurement.purpose === 'capacity' ? 'Distance Dc (cm) *' : 'Distance Dl (cm) *';
  const disLabel = measurement.purpose === 'capacity' ? 'De − Dc' : 'De − Dl';
  const step2Incomplete = fieldsLocked || formData.vesselType === '' || !!depthError || !result.complete;
  const step3Blocked = !result.complete || result.total == null || !result.totalInRange || porCheckStatus === 'flagged';
  const nextDisabled = currentStep === 1 && step2Incomplete || currentStep === 2 && step3Blocked;
  const nextDisabledReason = currentStep === 1 ? fieldsLocked ? 'Select a boat type first.' : formData.vesselType === '' ? 'Select a vessel type.' : depthError ? 'Fix the De / Dl measurement.' : 'Complete all measurement fields.' : currentStep === 2 ? porCheckStatus === 'flagged' ? 'Blocked by an active POR.' : 'Boat dimensions fall outside the standard BLC tables — refer to manual Example 3 (split-and-add) calculation.' : undefined;
  const updateMeasurement = (patch: Partial<BLCMeasurement>): void => {
    setMeasurement(prev => ({
      ...prev,
      ...patch
    }));
  };

  // Mock Data
  const blcList = [{
    blcNumber: 'BLC-2024-001',
    ownerName: 'Rahim Uddin',
    vesselType: 'Boat',
    loadKg: '4,730',
    annualFee: '৳ 50',
    issueDate: '2024-01-15',
    expiryDate: '2025-01-14',
    status: 'active' as const
  }, {
    blcNumber: 'BLC-2023-089',
    ownerName: 'Karim Sheikh',
    vesselType: 'Trawler',
    loadKg: '12,480',
    annualFee: '৳ 130',
    issueDate: '2023-05-20',
    expiryDate: '2024-05-19',
    status: 'expired' as const
  }];
  const columns = [{
    key: 'blcNumber',
    label: 'BLC Number'
  }, {
    key: 'ownerName',
    label: 'Owner Name'
  }, {
    key: 'vesselType',
    label: 'Boat Type'
  }, {
    key: 'loadKg',
    label: 'Load (Kg)'
  }, {
    key: 'annualFee',
    label: 'Annual Fee'
  }, {
    key: 'issueDate',
    label: 'Issue Date'
  }, {
    key: 'expiryDate',
    label: 'Expiry Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-554" data-mp="3gm3va7uhm5nq" />
  }];
  const wizardSteps = [{
    title: 'Owner Details',
    description: 'Enter owner NID and verify'
  }, {
    title: 'Boat Measurement',
    description: 'Record the BLC survey measurements (Le, Bf, Bm, Bb, De, Dl)'
  }, {
    title: 'POR Check & Fee',
    calculate: true,
    description: 'System computes load from the BLC log tables and calculates the annual fee'
  }, {
    title: 'Issue BLC',
    description: 'Generate digital BLC'
  }];
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'list',
    label: 'BLC Registry'
  }, {
    id: 'issue',
    label: 'Issue New BLC'
  }, {
    id: 'renew',
    label: 'Renew BLC'
  }, {
    id: 'master',
    label: 'Master Data'
  }];
  const measurementFields: {
    key: keyof BLCMeasurement;
    label: string;
    hint: string;
    step: string;
  }[] = [{
    key: 'le',
    label: 'Length of Waterline, Le (m) *',
    hint: 'Bow to stern at water level',
    step: '0.01'
  }, {
    key: 'bf',
    label: 'Width Bf (m) *',
    hint: '1/4 L from the front',
    step: '0.01'
  }, {
    key: 'bm',
    label: 'Width Bm (m) *',
    hint: '1/2 L, middle',
    step: '0.01'
  }, {
    key: 'bb',
    label: 'Width Bb (m) *',
    hint: '1/4 L from the back',
    step: '0.01'
  }, {
    key: 'de',
    label: 'Distance De (cm) *',
    hint: 'X-point to waterline, boat EMPTY',
    step: '0.1'
  }, {
    key: 'dl',
    label: dlLabel,
    hint: measurement.purpose === 'capacity' ? 'X-point to waterline at the set capacity mark' : 'X-point to waterline, boat LOADED',
    step: '0.1'
  }];
  return <div className="space-y-6" data-id="element-555" data-mp="3n8wkvb2l8yl4">
      <div data-id="element-556" data-mp="3n96bn0vgj9rv">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-557" data-mp="3n7lj1gspe0wn">BLC Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-558" data-mp="399omuli5vwii">
          Boat Load Capacity certification, renewal, and survey measurement
          logging
        </p>
      </div>

      <div className="border-b border-gray-200" data-id="element-559" data-mp="2zk537dmmjyml">
        <div className="flex gap-4" data-id="element-560" data-mp="2ziah3vzma0gx">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-561" data-mp="34cocvzi6y9m4">
              {tab.label}
            </button>)}
        </div>
      </div>

      {activeTab === 'list' && <DataTable columns={columns} data={blcList} searchPlaceholder="Search BLC or Owner..." data-id="element-562" data-mp="3123mkuukxoqk" />}

      {activeTab === 'master' && <BLCMasterData master={master} onChange={setMaster} data-id="element-563" data-mp="2s0w2bgoweofh" />}

      {activeTab === 'issue' && <WizardForm steps={wizardSteps} currentStep={currentStep} nextDisabled={nextDisabled} nextDisabledReason={nextDisabled ? nextDisabledReason : undefined} onStepChange={step => {
      if (step === 2 && currentStep === 1) {
        // POR / offender screening runs when entering the fee step
        setPorCheckStatus(Math.random() > 0.9 ? 'flagged' : 'clear');
      }
      setCurrentStep(step);
    }} onSubmit={() => {
      alert('Digital BLC Issued Successfully!');
      setCurrentStep(0);
      setActiveTab('list');
    }} data-id="element-564" data-mp="2n22jr9ur878g">
          {currentStep === 0 && <div className="space-y-4" data-id="element-565" data-mp="9vcq9812on1r">
              <div data-id="element-566" data-mp="9yiigw50051p">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-567" data-mp="eupnmmpklcil">
                  Vessel No. *
                </label>
                <input type="text" value={formData.vesselNo} onChange={e => {
            const vesselNo = e.target.value.toUpperCase();
            const vessel = vesselData[vesselNo];
            setFormData({
              ...formData,
              vesselNo,
              vesselType: vessel ? vessel.type : formData.vesselType
            });
            if (vessel) {
              updateMeasurement({
                hullType: vessel.hullType
              });
            }
          }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" placeholder="Enter Vessel No. (e.g., VSL-001)" data-id="element-568" data-mp="jvn32rj73sge" />
              </div>

              {formData.vesselNo && (matchedVessel ? <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-569" data-mp="1a7pzutwhzbjy">
                    <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2" data-id="element-570" data-mp="1a5wt7elvwrby">
                      <ShipIcon size={18} className="text-green-700" data-id="element-571" data-mp="1f4ggbic7s95w" />
                      Vessel Information
                    </h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-id="element-572" data-mp="mhncgr3j2tro">
                      <div data-id="element-573" data-mp="rh88asneim06">
                        <span className="text-gray-500" data-id="element-574" data-mp="rh00vga1s8wd">Vessel Name:</span>{' '}
                        <span className="font-medium text-gray-900" data-id="element-575" data-mp="w7oryuhtf3uz">
                          {matchedVessel.name}
                        </span>
                      </div>
                      <div data-id="element-576" data-mp="3uw4a54vxllkh">
                        <span className="text-gray-500" data-id="element-577" data-mp="3r5kioqis27d">Vessel Type:</span>{' '}
                        <span className="font-medium text-gray-900" data-id="element-578" data-mp="3sdip1pk9rply">
                          {matchedVessel.type}
                        </span>
                      </div>
                      <div data-id="element-579" data-mp="3nccwpsuiu4cy">
                        <span className="text-gray-500" data-id="element-580" data-mp="3if2ypot2gkdi">Registration No:</span>{' '}
                        <span className="font-medium text-gray-900" data-id="element-581" data-mp="3ih7ep548p9u5">
                          {matchedVessel.regNo}
                        </span>
                      </div>
                      <div data-id="element-582" data-mp="39fzf8jpzm2yh">
                        <span className="text-gray-500" data-id="element-583" data-mp="34ny799zuqye9">Owner:</span>{' '}
                        <span className="font-medium text-gray-900" data-id="element-584" data-mp="34mn49ce8duo7">
                          {matchedVessel.owner}
                        </span>
                      </div>
                      <div data-id="element-585" data-mp="2ur38qr3kwxzz">
                        <span className="text-gray-500" data-id="element-586" data-mp="2unp6rjytuqt2">BLC Boat Type:</span>{' '}
                        <span className="font-medium text-gray-900" data-id="element-587" data-mp="2qnng05umrhsk">
                          {matchedVessel.hullType}
                        </span>
                      </div>
                      <div data-id="element-588" data-mp="2c0ewmhr6465i">
                        <span className="text-gray-500" data-id="element-589" data-mp="2c06m2wiipcxh">Engine:</span>{' '}
                        <span className="font-medium text-gray-900" data-id="element-590" data-mp="2g3usvczbs4ho">
                          {matchedVessel.engineType} -{' '}
                          {matchedVessel.enginePower}
                        </span>
                      </div>
                      <div data-id="element-591" data-mp="27481lk0b8f2c">
                        <span className="text-gray-500" data-id="element-592" data-mp="2urfvl5sag1p2">Status:</span>{' '}
                        <span className="font-medium text-green-700" data-id="element-593" data-mp="2uubw91vit4h7">
                          {matchedVessel.status}
                        </span>
                      </div>
                    </div>
                  </div> : <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-594" data-mp="2q07ncd2p2mdh">
                    <p className="text-sm text-yellow-700" data-id="element-595" data-mp="3dqjz5z12939c">
                      No vessel found with number "
                      <span className="font-medium" data-id="element-596" data-mp="3iibv6p2zk16v">{formData.vesselNo}</span>".
                      Please check the vessel number and try again.
                    </p>
                  </div>)}

              <div data-id="element-597" data-mp="122yq478maohj">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-598" data-mp="121dueeakgzi3">
                  Owner NID *
                </label>
                <div className="flex gap-2" data-id="element-599" data-mp="1bsrpm9vhjd5f">
                  <input type="text" value={formData.ownerNid} onChange={e => setFormData({
              ...formData,
              ownerNid: e.target.value
            })} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" placeholder="Enter 10 or 13 digit NID" data-id="element-600" data-mp="1bttaf62aze0c" />
                  <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium" data-id="element-601" data-mp="28hpmpyqpjn5r">
                    Verify
                  </button>
                </div>
              </div>
              <div data-id="element-602" data-mp="2ywqnzjdvsuqy">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-603" data-mp="33tqozfvvj7b7">
                  Owner Name
                </label>
                <input type="text" value={formData.ownerName} onChange={e => setFormData({
            ...formData,
            ownerName: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-604" data-mp="38rsa06cv1gpb" />
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-5" data-id="element-605" data-mp="bp3w51ec033">
              <div className="grid gap-4 md:grid-cols-2" data-id="element-606" data-mp="eurffudmtwp">
                <div data-id="element-607" data-mp="5decle15c1o0">
                  <label htmlFor="vessel-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-608" data-mp="5a0c6bc1m2j7">
                    Vessel Type *
                  </label>
                  <select id="vessel-type" value={formData.vesselType} onChange={e => setFormData({
              ...formData,
              vesselType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-609" data-mp="1pu5hflkjpqqk">
                    <option value="" data-id="element-610" data-mp="28l5aoyz2pmpn">Select Vessel Type</option>
                    {VESSEL_TYPES.map(type => <option key={type} value={type} data-id="element-611" data-mp="23ru2ouiiyzw5">
                        {type}
                      </option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-612" data-mp="2wb9byzoshadh">
                    Vessel category recorded on the certificate. Does not affect
                    the load calculation.
                  </p>
                </div>
                <div data-id="element-613" data-mp="pmqvhsv539cr">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-614" data-mp="tnj7uuzb5ud9">
                    Boat Type *
                  </label>
                  <select value={measurement.hullType} onChange={e => updateMeasurement({
              hullType: e.target.value as BoatHullType | ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-615" data-mp="fv30xj3adc72">
                    <option value="" data-id="element-616" data-mp="3t3zc69e4s35w">Select Type</option>
                    {BOAT_TYPES.map(type => <option key={type} value={type} data-id="element-617" data-mp="1wwlrk64a0wq">
                        {type}
                      </option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-618" data-mp="1waxinzi3wdu6">
                    Determines which Le / Bav / Displacement log tables are used.
                    Type A and Type B use the manual's shared A/B values until
                    they are edited separately in Master Data.
                  </p>
                </div>
                <div data-id="element-619" data-mp="2f7fvgh6g4t3i">
                  <span className="block text-sm font-medium text-gray-700 mb-1" data-id="element-620" data-mp="2f8qzmi3t02v5">
                    Measurement Purpose
                  </span>
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" data-id="element-621" data-mp="2p4aqgq3igbd1">
                    <button type="button" onClick={() => updateMeasurement({
                purpose: 'actual'
              })} className={`px-3 py-2 text-sm font-medium transition-colors ${measurement.purpose === 'actual' ? 'bg-[#1B5E20] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-622" data-mp="2k691xpnbfju6">
                      Actual Load (use Dl)
                    </button>
                    <button type="button" onClick={() => updateMeasurement({
                purpose: 'capacity'
              })} className={`px-3 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${measurement.purpose === 'capacity' ? 'bg-[#1B5E20] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-623" data-mp="1t8m59jv19ctk">
                      Set Capacity (use Dc)
                    </button>
                  </div>
                </div>
              </div>

              <BoatMeasurementGuide data-id="element-624" data-mp="2prjppt3bgk0j" />

              {fieldsLocked && <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-625" data-mp="2uplaqjkayten">
                  <LockIcon size={16} data-id="element-626" data-mp="34wnwl850yhyk" />
                  Select a vessel type to unlock the measurement fields.
                </div>}

              <div className="grid gap-4 md:grid-cols-3" data-id="element-627" data-mp="2v3ymohp4hc5c">
                {measurementFields.map(field => <div key={field.key} data-id="element-628" data-mp="2r2n7l63vk9be">
                    <label htmlFor={`field-${field.key}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-629" data-mp="2r1lruobwyync">
                      {field.label}
                    </label>
                    <input id={`field-${field.key}`} type="number" step={field.step} disabled={fieldsLocked} value={String(measurement[field.key])} onChange={e => updateMeasurement({
              [field.key]: e.target.value
            } as Partial<BLCMeasurement>)} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1B5E20] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${field.key === 'dl' && depthError ? 'border-red-400' : 'border-gray-300'}`} data-id="element-630" data-mp="2c7782lkizl7m" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-631" data-mp="1qbsp4b6497z5">{field.hint}</p>
                  </div>)}
              </div>

              {depthError && <p className="text-sm text-red-600 font-medium flex items-start gap-2" data-id="element-632" data-mp="1hdqq215y0pei">
                  <AlertTriangleIcon size={16} className="mt-0.5" data-id="element-633" data-mp="1cffg7qfneewa" />
                  {depthError}
                </p>}

              <div className="grid gap-4 sm:grid-cols-2" data-id="element-634" data-mp="3bp5f0fr4qkt7">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-635" data-mp="36t6nijai8mok">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-636" data-mp="3ecfw4ab6shck">
                    Average Width, Bav
                  </div>
                  <div className="text-xl font-bold text-gray-900 mt-1" data-id="element-637" data-mp="3j8edvf20f140">
                    {result.bav ? `${result.bav.toFixed(2)} m` : '—'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-638" data-mp="3t00yg208mcjf">
                    AVERAGE(Bf, Bm, Bb)
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-639" data-mp="30bve3gd3ipk4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-640" data-mp="30dpzevsa4joq">
                    Water Displacement, Dis
                  </div>
                  <div className="text-xl font-bold text-gray-900 mt-1" data-id="element-641" data-mp="39dcp4kbnc11y">
                    {Number.isFinite(de) && Number.isFinite(dl) && !depthError ? `${result.dis.toFixed(2)} cm` : '—'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-642" data-mp="2lmq85fx2pk7w">{disLabel}</p>
                </div>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-643" data-mp="2gq83sh5nnnw3">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-644" data-mp="2unwo9nvt8t0m">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-645" data-mp="2po20vub8ulkb">
                  <CalculatorIcon size={20} className="text-[#1B5E20]" data-id="element-646" data-mp="21yptmzzh8nkn" />
                  Calculated Metrics
                </h4>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-647" data-mp="1xyfb6nui04ow">
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-648" data-mp="2boivvadqbqc8">
                    <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-649" data-mp="26y2fpiiixqm6">
                      Load (Kg)
                    </div>
                    <div className="text-lg font-bold text-gray-900" data-id="element-650" data-mp="1j7g8tuk9acdr">
                      {result.loadKg?.toLocaleString() ?? '—'}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-651" data-mp="3pw8foq2v4mkp">
                    <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-652" data-mp="3ungslw3eiq2m">
                      Load (Maund)
                    </div>
                    <div className="text-lg font-bold text-gray-900" data-id="element-653" data-mp="3gxd7j8g9d2er">
                      {result.loadMaund?.toLocaleString() ?? '—'}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-654" data-mp="dgel4rpch1x4">
                    <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-655" data-mp="hhglxcywtm17">
                      Load (Tons)
                    </div>
                    <div className="text-lg font-bold text-gray-900" data-id="element-656" data-mp="3gxhsgxkf04b">
                      {result.loadTons?.toFixed(2) ?? '—'}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-657" data-mp="2o6d7f8l1sisd">
                    <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-658" data-mp="2t6reanawxmot">
                      Billable Units
                    </div>
                    <div className="text-lg font-bold text-gray-900" data-id="element-659" data-mp="38i8wnrny83gh">
                      {result.billableUnits ?? '—'}
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border border-gray-200" data-id="element-660" data-mp="2zom38877rcag">
                    <div className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-661" data-mp="2upiyypz25a3j">
                      Annual Fee (Tk)
                    </div>
                    <div className="text-lg font-bold text-[#1B5E20]" data-id="element-662" data-mp="3rcw8xaaap3g1">
                      {result.annualFee != null ? `৳ ${result.annualFee}` : '—'}
                    </div>
                  </div>
                </div>
              </div>

              {result.warnings.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2" data-id="element-663" data-mp="3dezdwibhp53h">
                  <h4 className="font-semibold text-red-800 flex items-center gap-2" data-id="element-664" data-mp="270q6j3hn9kdo">
                    <AlertTriangleIcon size={18} data-id="element-665" data-mp="21zaq5oqew21m" />
                    Measurement out of table range — needs manual review
                  </h4>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1" data-id="element-666" data-mp="1wv88lloqegin">
                    {result.warnings.map(warning => <li key={warning} data-id="element-667" data-mp="21tjhnu77cmzt">{warning}</li>)}
                  </ul>
                </div>}

              {/* Transparency / audit panel */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-668" data-mp="2anem100i29ee">
                <button type="button" onClick={() => setShowBreakdown(!showBreakdown)} aria-expanded={showBreakdown} className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors" data-id="element-669" data-mp="2fl6q0sef5lyy">
                  <span className="font-semibold text-gray-900 text-sm" data-id="element-670" data-mp="q7js75m1z8ne">
                    How this fee was calculated
                  </span>
                  <ChevronDownIcon size={18} className={`text-gray-500 transition-transform ${showBreakdown ? 'rotate-180' : ''}`} data-id="element-671" data-mp="v7z37qiv4427" />
                </button>
                {showBreakdown && <div className="px-4 py-4 border-t border-gray-200 bg-gray-50" data-id="element-672" data-mp="x3mkj10byubm">
                    <table className="w-full text-sm" data-id="element-673" data-mp="s3qjnamxjj0a">
                      <tbody data-id="element-674" data-mp="s2you215ppb2">
                        {[{
                  label: 'Vessel type',
                  value: formData.vesselType || '—'
                }, {
                  label: 'Boat type (table set)',
                  value: measurement.hullType || '—'
                }, {
                  label: 'Le (waterline length)',
                  value: `${result.le.value || 0} m`
                }, {
                  label: 'Bav = AVERAGE(Bf, Bm, Bb)',
                  value: `${result.bav.toFixed(2)} m`
                }, {
                  label: `Dis = ${disLabel}`,
                  value: `${result.dis.toFixed(2)} cm`
                }, {
                  label: 'LogLe (from Le table)',
                  value: result.le.log?.toFixed(4) ?? 'out of range'
                }, {
                  label: 'LogBav (from Bav table)',
                  value: result.bavLookup.log?.toFixed(4) ?? 'out of range'
                }, {
                  label: 'LogDis (from displacement table)',
                  value: result.disLookup.log?.toFixed(4) ?? 'out of range'
                }, {
                  label: 'Total = ROUND(LogLe + LogBav + LogDis, 2)',
                  value: result.total?.toFixed(2) ?? '—'
                }, {
                  label: 'Load (Kg) from antilog table',
                  value: result.loadKg?.toLocaleString() ?? 'out of range'
                }, {
                  label: `Load (Maund) = Kg ÷ ${master.maundFactor}`,
                  value: result.loadMaund?.toLocaleString() ?? '—'
                }, {
                  label: `Billable Units = ROUNDUP(Kg ÷ ${master.feeConfig.unitKg})`,
                  value: result.billableUnits?.toString() ?? '—'
                }, {
                  label: `Annual Fee = Units × ৳ ${master.feeConfig.ratePerUnit}`,
                  value: result.annualFee != null ? `৳ ${result.annualFee}` : '—'
                }].map(row => <tr key={row.label} className="border-b border-gray-200 last:border-0" data-id="element-675" data-mp="356ms69o0yfyz">
                            <td className="py-1.5 pr-4 text-gray-600" data-id="element-676" data-mp="2dfyuj7lk7sq2">
                              {row.label}
                            </td>
                            <td className="py-1.5 text-right font-semibold text-gray-900 tabular-nums" data-id="element-677" data-mp="2hgsghvffyxgb">
                              {row.value}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                    <p className="text-xs text-gray-500 mt-3" data-id="element-678" data-mp="1tqy75nubzr0t">
                      Fee rate effective {master.feeConfig.effectiveDate}. Table
                      values are maintained under the Master Data tab.
                    </p>
                  </div>}
              </div>

              <div className={`p-4 rounded-lg border ${porCheckStatus === 'flagged' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-id="element-679" data-mp="uhdtlymo2tl2">
                <h4 className={`font-semibold mb-2 flex items-center gap-2 ${porCheckStatus === 'flagged' ? 'text-red-800' : 'text-green-800'}`} data-id="element-680" data-mp="kr1bed2brci3">
                  {porCheckStatus === 'flagged' ? <>
                      <ShieldAlertIcon size={20} data-id="element-681" data-mp="gqil4ng2yei0" /> Active POR Found
                    </> : <>
                      <CheckCircleIcon size={20} data-id="element-682" data-mp="1u9ozurhkh9t" /> Offender Check Passed
                    </>}
                </h4>
                <p className={`text-sm ${porCheckStatus === 'flagged' ? 'text-red-700' : 'text-green-700'}`} data-id="element-683" data-mp="6ufg99tug1rd">
                  {porCheckStatus === 'flagged' ? 'Owner is flagged in the Offender Module. BLC issuance is blocked until cleared.' : 'No active Forest Offense Reports found for this owner. Safe to proceed.'}
                </p>
              </div>

              {porCheckStatus === 'flagged' && <div className="text-sm text-red-600 font-medium" data-id="element-684" data-mp="63kj185v1fp4">
                  Cannot proceed to next step due to active POR.
                </div>}
            </div>}

          {currentStep === 3 && <div className="space-y-6" data-id="element-685" data-mp="aw3v20owg2rt">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-id="element-686" data-mp="ax598e17p5dr">
                <CheckCircleIcon size={48} className="mx-auto text-green-600 mb-4" data-id="element-687" data-mp="pufrq7c9vbiz" />
                <h3 className="text-lg font-bold text-green-900 mb-2" data-id="element-688" data-mp="ks6xl7nny3fa">
                  Ready to Issue Digital BLC
                </h3>
                <p className="text-sm text-green-700 mb-4" data-id="element-689" data-mp="ww1h5awrmgvl">
                  Load {result.loadKg?.toLocaleString()} Kg (
                  {result.loadMaund?.toLocaleString()} maund) · Annual fee ৳{' '}
                  {result.annualFee}. The BLC will be linked to the owner's
                  Smart ID.
                </p>
                <div className="inline-block bg-white px-4 py-2 rounded-lg border border-green-200 font-mono font-bold text-lg text-gray-900" data-id="element-690" data-mp="1bkfkro20a9d7">
                  BLC-2024-
                  {Math.floor(Math.random() * 1000).toString().padStart(3, '0')}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'renew' && <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-691" data-mp="3mfcwkhqcyg3q">
          <h3 className="text-lg font-semibold mb-4" data-id="element-692" data-mp="3rdef91jvgdeo">BLC Renewal Workflow</h3>
          <div className="flex gap-4 mb-6" data-id="element-693" data-mp="3jtc8gxyar1k5">
            <input type="text" placeholder="Enter existing BLC Number" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20]" data-id="element-694" data-mp="3jueyz4rpj1zj" />
            <button className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-695" data-mp="3547pvfnlavbi">
              Search
            </button>
          </div>
          <div className="text-center text-gray-500 py-8" data-id="element-696" data-mp="6l989ktg1m99">
            Search for an existing BLC to initiate the renewal process.
          </div>
        </div>}
    </div>;
}